import axios from 'axios';
import { useLocation, useParams } from 'react-router-dom';
import { TURNSTILE_RESET_EVENT } from './turnstile';

const INBOUNDS_CAPTURE_URL = process.env.REACT_APP_INBOUNDS_CAPTURE_URL;

export const snackDefaultConfig = {
  autoHideDuration: 3000,
  anchorOrigin: { horizontal: 'right', vertical: 'top' },
};

export const createAntiBotInitialValues = () => ({
  company_website: '',
  form_loaded_at: String(Math.floor(Date.now() / 1000)),
  turnstile_token: '',
});

export const getAntiBotPayload = (values = {}) => ({
  company_website:
    typeof values.company_website === 'string' ? values.company_website : '',
  form_loaded_at: values.form_loaded_at ?? '',
  turnstile_token:
    typeof values.turnstile_token === 'string' ? values.turnstile_token : '',
});

export const sendEmail = async (
  sender,
  content,
  fnSnackbar,
  lead = {},
  antiBot = {},
) => {
  if (!INBOUNDS_CAPTURE_URL) {
    console.error('REACT_APP_INBOUNDS_CAPTURE_URL is not configured.');

    fnSnackbar('Failed to send your request email.', {
      variant: 'error',
      ...snackDefaultConfig,
    });

    return false;
  }

  const headers = {
    'Content-Type': 'application/json',
  };

  const { subject, message } = content;
  try {
    await axios.post(
      INBOUNDS_CAPTURE_URL,
      {
        type: lead.type || 'contact_form',
        email: sender,
        name: lead.name || 'Website Visitor',
        phone: lead.phone || '',
        company: lead.company || '',
        subject,
        message,
        company_website: antiBot.company_website || '',
        form_loaded_at: antiBot.form_loaded_at || '',
        turnstile_token: antiBot.turnstile_token || '',
      },
      {
        headers,
      },
    );

    fnSnackbar('Your request email has been sent successfully.', {
      variant: 'success',
      ...snackDefaultConfig,
    });

    return true;
  } catch (err) {
    console.error(err);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(TURNSTILE_RESET_EVENT));
    }

    fnSnackbar('Failed to send your request email.', {
      variant: 'error',
      ...snackDefaultConfig,
    });
  }

  return false;
};

// UX-only early feedback. The server has the authoritative equivalent list in
// server/capture-protection.js; intentional differences must remain safe server-side.
const BLOCKED_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'pm.me',
  'tutanota.com',
  'tutanota.de',
  'tuta.com',
  'fastmail.com',
  'mail.com',
]);

const BLOCKED_DOMAIN_PREFIXES = [
  'hotmail.',
  'outlook.',
  'live.',
  'msn.',
  'yahoo.',
  'ymail.',
  'rocketmail.',
  'gmx.',
  'yandex.',
];

const BLOCKED_DOMAIN_SUFFIXES = [
  '.gmail.com',
  '.googlemail.com',
  '.protonmail.com',
  '.proton.me',
  '.pm.me',
  '.tutanota.com',
  '.tutanota.de',
  '.tuta.com',
  '.mail.com',
  '.fastmail.com',
];

export const checkPrivateEmail = (value) => {
  try {
    const domain = value.split('@')[1]?.trim().toLowerCase();
    if (!domain) return false;

    if (BLOCKED_EMAIL_DOMAINS.has(domain)) return false;

    if (BLOCKED_DOMAIN_PREFIXES.some((prefix) => domain.startsWith(prefix))) {
      return false;
    }

    if (
      BLOCKED_DOMAIN_SUFFIXES.some(
        (suffix) => domain.endsWith(suffix),
      )
    ) {
      return false;
    }

    return true;
  } catch (err) {
    return false;
  }
};

export const gridCol = (option) => {
  let colspanCls = 'col-span-12';
  colspanCls += option.tablet ? ' sm:col-span-' + 12 / option.tablet : '';
  colspanCls += option.laptop ? ' lg:col-span-' + 12 / option.laptop : '';
  colspanCls += option.desktop ? ' xl:col-span-' + 12 / option.desktop : '';

  return colspanCls;
};

export const fadeIn = (element, duration) => {
  let opacity = 0;
  const interval = 20; // Adjust as needed for smoothness

  const timer = setInterval(() => {
    if (opacity >= 1) {
      clearInterval(timer);
    }

    element.style.opacity = opacity;
    opacity += interval / duration;
  }, interval);
};

export const fadeOut = (element, duration) => {
  let opacity = 1;
  const interval = 20; // Adjust as needed for smoothness

  const timer = setInterval(() => {
    if (opacity <= 0) {
      clearInterval(timer);
    }

    element.style.opacity = opacity;
    opacity -= interval / duration;
  }, interval);
};

export const useBasePath = () => {
  const location = useLocation();
  const params = useParams();

  return Object.values(params).reduce(
    (path, param) => path.replace('/' + param, ''),
    location.pathname,
  );
};

export const formatNumber = (num) => {
  if (Math.abs(num) < 1024) {
    return num.toString(); // Numbers less than 1000 are displayed as is
  } else if (Math.abs(num) >= 1024 && Math.abs(num) < 1024 * 1024) {
    return (num / 1024).toFixed(2) + ' kB'; // Display numbers in thousands
  } else if (Math.abs(num) >= 1024 * 1024 && Math.abs(num) < 1024 * 1024 * 1024) {
    return (num / 1024 / 1024).toFixed(2) + ' MB'; // Display numbers in millions
  } else if (Math.abs(num) >= 1024 * 1024 * 1024 && Math.abs(num) < 1024 * 1024 * 1024 * 1024) {
    return (num / 1024 / 1024 / 1024).toFixed(2) + ' GB'; // Display numbers in billions
  } else {
    return (num / 1024 / 1024 / 1024 / 1024).toFixed(2) + ' TB'; // Display numbers in trillions
  }
}
